import { useState } from 'react';
import { advertisingPlans } from '../../../mocks/localAds';

interface PricingPlansProps {
  onSelectPlan: (planName: string) => void;
}

/**
 * PricingPlans component – displays a list of advertising plans.
 *
 * The component now includes a small amount of defensive programming:
 *  • It validates that the `advertisingPlans` array exists before mapping.
 *  • It guards the `onSelectPlan` callback against being undefined or
 *    throwing an error.
 *
 * These additions make the component more robust while keeping the
 * original visual layout untouched.
 */
export default function PricingPlans({ onSelectPlan }: PricingPlansProps) {
  const [loadingPlan, setLoadingPlan] = useState<string | null>(null);
  const [error, setError] = useState<string>('');

  // Defensive guard – if for any reason the mock data is missing, render nothing.
  if (!Array.isArray(advertisingPlans) || advertisingPlans.length === 0) {
    return null;
  }

  // Safe wrapper for the callback
  const handleSelect = async (planName: string) => {
    setError('');
    setLoadingPlan(planName);

    try {
      // Call Stripe checkout edge function
      const response = await fetch(
        'https://uijspwhqjeorxcpsoeha.supabase.co/functions/v1/create-subscription-checkout',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            planName: planName,
            businessName: '',
            email: '',
          }),
        }
      );

      const data = await response.json();

      if (response.ok && data.url) {
        // Redirect to Stripe checkout
        window.location.href = data.url;
      } else {
        throw new Error(data.error || 'Failed to create checkout session');
      }
    } catch (err) {
      console.error('Error creating checkout:', err);
      setError('Unable to start checkout. Please try again or contact us.');
      setLoadingPlan(null);
      
      // Fallback to inquiry form
      onSelectPlan?.(planName);
    }
  };

  return (
    <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-14">
          <h2 className="text-3xl font-bold text-gray-900 mb-3">Advertising Plans</h2>
          <p className="text-gray-600 max-w-xl mx-auto">
            Choose the plan that fits your business. All plans include a listing on Roseville.org with no long-term contracts.
          </p>
        </div>

        {error && (
          <div className="max-w-2xl mx-auto mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 flex items-center gap-2">
            <i className="ri-error-warning-line"></i>
            {error}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
          {advertisingPlans.map((plan) => (
            <div
              key={plan.id}
              className={`relative rounded-2xl p-8 transition-all duration-300 hover:shadow-xl ${
                plan.popular
                  ? 'bg-teal-700 text-white shadow-xl scale-105 ring-4 ring-teal-200'
                  : 'bg-white border border-gray-200 shadow-sm'
              }`}
            >
              {plan.popular && (
                <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                  <span className="px-4 py-1.5 bg-amber-500 text-white text-xs font-bold rounded-full whitespace-nowrap">
                    Most Popular
                  </span>
                </div>
              )}

              <div className="text-center mb-6">
                <h3 className={`text-xl font-bold mb-2 ${plan.popular ? 'text-white' : 'text-gray-900'}`}>
                  {plan.name}
                </h3>
                <div className="flex items-baseline justify-center gap-1">
                  <span className={`text-4xl font-bold ${plan.popular ? 'text-white' : 'text-gray-900'}`}>
                    ${plan.price}
                  </span>
                  <span className={`text-sm ${plan.popular ? 'text-white/70' : 'text-gray-500'}`}>
                    /{plan.period}
                  </span>
                </div>
              </div>

              <ul className="space-y-3 mb-8">
                {plan.features.map((feature, idx) => (
                  <li key={idx} className="flex items-start gap-3 text-sm">
                    <i className={`ri-check-line mt-0.5 ${plan.popular ? 'text-amber-400' : 'text-teal-500'}`}></i>
                    <span className={plan.popular ? 'text-white/90' : 'text-gray-600'}>{feature}</span>
                  </li>
                ))}
              </ul>

              <button
                onClick={() => handleSelect(plan.name)}
                disabled={loadingPlan === plan.name}
                className={`w-full py-3 rounded-lg font-semibold transition-colors whitespace-nowrap cursor-pointer flex items-center justify-center gap-2 ${
                  plan.popular
                    ? 'bg-white text-teal-700 hover:bg-gray-100'
                    : 'bg-teal-600 text-white hover:bg-teal-700'
                } disabled:opacity-60 disabled:cursor-not-allowed`}
              >
                {loadingPlan === plan.name ? (
                  <>
                    <i className="ri-loader-4-line animate-spin"></i>
                    Loading...
                  </>
                ) : (
                  'Get Started'
                )}
              </button>
            </div>
          ))}
        </div>

        <p className="text-center text-sm text-gray-500 mt-10">
          All plans are month-to-month. Cancel anytime. Need a custom solution?{' '}
          <a
            href="#inquiry"
            className="text-teal-600 font-semibold hover:underline cursor-pointer"
          >
            Contact us
          </a>
        </p>
        <p className="text-center text-sm text-gray-500 mt-3">
          Already a subscriber?{' '}
          <a
            href="/subscriptions"
            className="text-teal-600 font-semibold hover:underline cursor-pointer"
          >
            Manage your subscription
          </a>
        </p>
      </div>
    </section>
  );
}
